import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import PopoutWindow from '../common/PopoutWindow';
import InvoicePreviewDialog from './InvoicePreviewDialog';
import AccountsSettings from './AccountsSettings';
import JobForm from '../documentation/JobForm';
import { openFilePreview } from '../common/FilePreviewModal';
import PayableExpensesView from '../admin/PayableExpensesView';
import { INVOICING_RELEASED_JOBS_START_DATE } from '../../constants/constants';
import '../documentation/JobList.css';
import * as XLSX from 'xlsx-js-style';
import ConfirmDialog from '../common/ConfirmDialog';

const API_BASE = '/api/documentation/jobs';

const toDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value).toUpperCase();
};

const cellStyle = (value) => {
  const shouldWrap = String(value || '').length > 20;
  return {
    border: '1px solid #ddd',
    padding: '6px',
    whiteSpace: shouldWrap ? 'normal' : 'nowrap',
    wordBreak: shouldWrap ? 'break-word' : 'normal',
    maxWidth: 180,
  };
};

const parseAmount = (value) => {
  const n = parseFloat(String(value || '0').replace(/,/g, ''));
  return Number.isNaN(n) ? 0 : n;
};

const BL_DOC_KEYWORDS = ['lading', 'awb', 'rcn', 'bl'];

const getBlDocument = (row) => {
  if (!row) return null;
  if (Array.isArray(row.documents) && row.documents.length > 0) {
    const doc = row.documents.find(d => {
      if (!d.document_type) return false;
      const type = d.document_type.toLowerCase();
      return BL_DOC_KEYWORDS.some(kw => type.includes(kw));
    });
    if (doc) {
      const url = doc.file_url || doc.document_file_url || doc.file;
      if (url) {
        return {
          url,
          name: `${doc.document_type} - ${row.bl || row.idf_no || row.id}`
        };
      }
    }
  }
  if (row.bl_file_url) {
    return { url: row.bl_file_url, name: `BL - ${row.bl || row.idf_no || row.id}` };
  }
  return null;
};

const formatSummaryAmount = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseGeneratedDate = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) {
    const day = parseInt(m[1], 10);
    const month = parseInt(m[2], 10) - 1;
    const year = parseInt(m[3], 10);
    return new Date(year, month, day);
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;
  return null;
};

const formatDateDDMMYYYY = (value) => {
  const d = parseGeneratedDate(value);
  if (!d) return value || '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const getGeneratedMonthKey = (value) => {
  const d = parseGeneratedDate(value);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthYearLabel = (key) => {
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) return key;
  return new Date(year, month - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
};

const todayISO = () => new Date().toISOString().split('T')[0];

const addDays = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatISODate = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB') : '-';

const GENERATED_VIEW_MODES = [
  { id: 'daily', label: 'Daily' },
  { id: 'minus1d', label: '-1D' },
  { id: 'minus1w', label: '-1W' },
  { id: 'minus1m', label: '-1M' },
  { id: 'minus3m', label: '-3M' },
  { id: 'range', label: 'Date Range' },
];

const Invoicing = () => {
  const { hasPermission, permissions } = usePermissions();
  const canView = hasPermission('INVOICING', 'can_view');
  const canCreate = hasPermission('INVOICING', 'can_create') || hasPermission('INVOICING', 'can_edit');
  const canEdit = hasPermission('INVOICING', 'can_edit');
  const canDelete = hasPermission('INVOICING', 'can_delete');
  const userDepartment = (permissions.department_name || permissions.department || '').toUpperCase();
  const canSearchNonReleased = permissions.is_superuser || userDepartment === 'ADMIN';
  const showViewExpenses = canCreate;

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingRows, setPendingRows] = useState([]);
  const [generatedRows, setGeneratedRows] = useState([]);
  const [pendingLoaded, setPendingLoaded] = useState(false);
  const [generatedLoaded, setGeneratedLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [searchNonReleased, setSearchNonReleased] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewJobId, setPreviewJobId] = useState(null);
  const [additionalOpen, setAdditionalOpen] = useState(false);
  const [additionalRow, setAdditionalRow] = useState(null);
  const [expensesModal, setExpensesModal] = useState(null);
  const [selected, setSelected] = useState([]);
  const [zModalOpen, setZModalOpen] = useState(false);
  const [zNumber, setZNumber] = useState('');
  const [zDate, setZDate] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [jobView, setJobView] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingUploadJobId, setPendingUploadJobId] = useState(null);
  const [pendingUploadAdditionalInvoiceId, setPendingUploadAdditionalInvoiceId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editZNumber, setEditZNumber] = useState('');
  const [editZDate, setEditZDate] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [generatedPage, setGeneratedPage] = useState('current');
  const [generatedViewMode, setGeneratedViewMode] = useState('daily');
  const [generatedStartDate, setGeneratedStartDate] = useState(todayISO());
  const [generatedEndDate, setGeneratedEndDate] = useState(todayISO());
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState({ open: false, jobId: null });
  const [reverseConfirmDialog, setReverseConfirmDialog] = useState({ open: false, jobId: null });
  const fileInputRef = useRef(null);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/invoicing-customers/`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch customers');
      const data = await res.json();
      const allCustomers = (Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), text: c.name, name: c.name }));
      setCustomers(allCustomers);
    } catch (e) {
      toast.error(e.message || 'Failed to load customers');
    }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const fetchRows = useCallback(async (silent = false) => {
    if (!silent) {
      setPendingLoaded(false);
      setGeneratedLoaded(false);
    }
    try {
      const params = new URLSearchParams();
      params.append('customer_id', customerId || 'ALL');
      if (appliedSearch) params.append('search', appliedSearch);
      if (searchNonReleased) params.append('non_released', 'true');
      if (INVOICING_RELEASED_JOBS_START_DATE) params.append('released_from', INVOICING_RELEASED_JOBS_START_DATE);
      const pendingUrl = `${API_BASE}/invoicing-pending/?${params.toString()}`;
      const generatedUrl = `${API_BASE}/invoicing-generated/?${params.toString()}`;
      const fetchPending = async () => {
        try {
          const res = await fetch(pendingUrl, { headers: getAuthHeaders(false) });
          if (!res.ok) throw new Error('Failed to fetch pending invoicing data');
          const data = await res.json();
          setPendingRows(data.rows || []);
        } finally {
          setPendingLoaded(true);
        }
      };
      const fetchGenerated = async () => {
        try {
          const res = await fetch(generatedUrl, { headers: getAuthHeaders(false) });
          if (!res.ok) throw new Error('Failed to fetch generated invoicing data');
          const data = await res.json();
          setGeneratedRows(data.rows || []);
        } finally {
          setGeneratedLoaded(true);
        }
      };
      // Only wait for the active tab's data; the other tab loads in the background
      // and its rows render as soon as they arrive.
      if (activeTab === 'pending') {
        fetchGenerated().catch(() => {});
        await fetchPending();
      } else {
        fetchPending().catch(() => {});
        await fetchGenerated();
      }
    } catch (e) {
      if (!silent) toast.error(e.message || 'Failed to fetch invoicing data');
      if (!silent) {
        setPendingRows([]);
        setGeneratedRows([]);
      }
    }
  }, [customerId, appliedSearch, searchNonReleased]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const today = todayISO();
    let start = today;
    let end = today;
    if (generatedViewMode === 'minus1d') {
      start = addDays(today, -1);
      end = today;
    } else if (generatedViewMode === 'minus1w') {
      start = addDays(today, -6);
      end = today;
    } else if (generatedViewMode === 'minus1m') {
      start = addDays(today, -29);
      end = today;
    } else if (generatedViewMode === 'minus3m') {
      start = addDays(today, -89);
      end = today;
    }
    setGeneratedStartDate(start);
    setGeneratedEndDate(end);
    setGeneratedPage('current');
  }, [generatedViewMode]);

  const handleCustomerChange = (val) => { setCustomerId(val || ''); setSearch(''); setAppliedSearch(''); setSearchNonReleased(false); };
  const handleApplySearch = () => { setAppliedSearch(search); if (search.trim()) setActiveTab('pending'); };
  const handleClear = () => { setSearch(''); setAppliedSearch(''); setSearchNonReleased(false); };
  const pendingZRows = useMemo(() => generatedRows.filter(r => !r.complimentary && (!r.z_number || !r.z_date || !r.tax_invoice_url)), [generatedRows]);
  const completedGeneratedRows = useMemo(() => generatedRows.filter(r => !r.complimentary && r.z_number && r.z_date && r.tax_invoice_url), [generatedRows]);
  const complimentaryRows = useMemo(() => generatedRows.filter(r => r.complimentary), [generatedRows]);
  const generatedSummary = useMemo(() => {
    const count = completedGeneratedRows.length;
    const net = completedGeneratedRows.reduce((sum, r) => sum + parseAmount(r.subtotal), 0);
    const vat = completedGeneratedRows.reduce((sum, r) => sum + parseAmount(r.vat), 0);
    const gross = completedGeneratedRows.reduce((sum, r) => sum + parseAmount(r.total_amount), 0);
    return { count, gross, vat, net };
  }, [completedGeneratedRows]);

  const firstGeneratedDate = useMemo(() => {
    if (!completedGeneratedRows.length) return null;
    const minTime = Math.min(...completedGeneratedRows.map(r => {
      const d = parseGeneratedDate(r.generated_at);
      return d ? d.getTime() : Infinity;
    }));
    const d = new Date(minTime);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [completedGeneratedRows]);

  const rows = activeTab === 'pending' ? pendingRows : activeTab === 'pendingZ' ? pendingZRows : activeTab === 'complimentary' ? complimentaryRows : completedGeneratedRows;
  const activeTabLoading = activeTab === 'pending' ? !pendingLoaded : !generatedLoaded;
  const [sortColumn, setSortColumn] = useState('release_date');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    if (activeTab === 'pending' || activeTab === 'complimentary') {
      setSortColumn('release_date');
      setSortDirection('desc');
    } else {
      setSortColumn('generated_at');
      setSortDirection('desc');
    }
  }, [activeTab]);

  const displayedRows = useMemo(() => {
    if (!sortColumn) return rows;
    const isDateColumn = ['generated_at', 'release_date', 'z_date'].includes(sortColumn);
    const getSortValue = (row) => {
      const v = row[sortColumn];
      if (isDateColumn) {
        const d = parseGeneratedDate(v);
        return d ? d.getTime() : null;
      }
      return String(v || '').toUpperCase();
    };
    const sorted = [...rows].sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);
      if (av === null && bv === null) return 0;
      if (av === null) return sortDirection === 'asc' ? 1 : -1;
      if (bv === null) return sortDirection === 'asc' ? -1 : 1;
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, sortColumn, sortDirection]);

  const selectedSummary = useMemo(() => {
    const sel = displayedRows.filter(r => selected.includes(r.id));
    const count = sel.length;
    const net = sel.reduce((sum, r) => sum + parseAmount(r.subtotal), 0);
    const vat = sel.reduce((sum, r) => sum + parseAmount(r.vat), 0);
    const gross = sel.reduce((sum, r) => sum + parseAmount(r.total_amount), 0);
    return { count, gross, vat, net };
  }, [displayedRows, selected]);

  const currentMonthRows = useMemo(() => {
    if (activeTab !== 'generated') return [];
    if (!generatedStartDate || !generatedEndDate) return displayedRows;
    const start = new Date(`${generatedStartDate}T00:00:00`);
    const end = new Date(`${generatedEndDate}T00:00:00`);
    return displayedRows.filter(r => {
      const d = parseGeneratedDate(r.generated_at);
      if (!d) return false;
      const g = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      return g >= start && g <= end;
    });
  }, [displayedRows, activeTab, generatedStartDate, generatedEndDate]);

  const previousMonthGroups = useMemo(() => {
    if (activeTab !== 'generated') return [];
    const currentIds = new Set(currentMonthRows.map(r => r.id));
    const previousRows = displayedRows.filter(r => !currentIds.has(r.id));
    const map = new Map();
    previousRows.forEach(r => {
      const key = getGeneratedMonthKey(r.generated_at);
      if (!key) return;
      if (!map.has(key)) map.set(key, { key, label: formatMonthYearLabel(key), rows: [] });
      map.get(key).rows.push(r);
    });
    return Array.from(map.values()).sort((a, b) => b.key.localeCompare(a.key));
  }, [displayedRows, activeTab, currentMonthRows]);

  const generatedDateRangeSummary = useMemo(() => {
    const count = currentMonthRows.length;
    const net = currentMonthRows.reduce((sum, r) => sum + parseAmount(r.subtotal), 0);
    const vat = currentMonthRows.reduce((sum, r) => sum + parseAmount(r.vat), 0);
    const gross = currentMonthRows.reduce((sum, r) => sum + parseAmount(r.total_amount), 0);
    return { count, gross, vat, net };
  }, [currentMonthRows]);

  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const toggleMonth = (key) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewJobId(null);
  }, []);

  const closeAdditional = useCallback(() => {
    setAdditionalOpen(false);
    setAdditionalRow(null);
  }, []);

  const openAdditionalInvoice = useCallback((row) => {
    setAdditionalRow(row);
    setAdditionalOpen(true);
  }, []);

  const refreshGeneratedRows = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('customer_id', customerId || 'ALL');
      if (appliedSearch) params.append('search', appliedSearch);
      if (searchNonReleased) params.append('non_released', 'true');
      if (INVOICING_RELEASED_JOBS_START_DATE) params.append('released_from', INVOICING_RELEASED_JOBS_START_DATE);
      const res = await fetch(`${API_BASE}/invoicing-generated/?${params.toString()}`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch generated invoices');
      const data = await res.json();
      setGeneratedRows(data.rows || []);
    } catch (e) {
      // silent: avoid breaking the flow after successful generation
    }
  }, [customerId, appliedSearch, searchNonReleased]);

  const handleAdditionalGenerated = useCallback(() => {
    refreshGeneratedRows();
  }, [refreshGeneratedRows]);

  const handleGenerated = useCallback((jobId) => {
    const currentRows = displayedRows;
    const idx = currentRows.findIndex(r => r.id === jobId);
    const remainingRows = currentRows.filter(r => r.id !== jobId);
    const nextId = remainingRows[idx]?.id || remainingRows[0]?.id || null;
    setPendingRows(prev => prev.filter(r => r.id !== jobId));
    setHighlightedId(nextId);
    refreshGeneratedRows();
  }, [displayedRows, refreshGeneratedRows]);

  const openComplimentaryConfirm = (jobId) => setConfirmDialog({ open: true, jobId });
  const closeComplimentaryConfirm = () => setConfirmDialog({ open: false, jobId: null });
  const openReverseComplimentaryConfirm = (jobId) => setReverseConfirmDialog({ open: true, jobId });
  const closeReverseComplimentaryConfirm = () => setReverseConfirmDialog({ open: false, jobId: null });

  const handleMarkComplimentary = useCallback(async () => {
    const { jobId } = confirmDialog;
    if (!jobId) return;
    try {
      const res = await fetch(`${API_BASE}/invoicing-mark-complimentary/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(false), 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to mark as complimentary');
      toast.success('Invoice marked as complimentary.');
      fetchRows();
    } catch (e) {
      toast.error(e.message || 'Failed to mark as complimentary');
    } finally {
      closeComplimentaryConfirm();
    }
  }, [confirmDialog, fetchRows]);

  const handleReverseComplimentary = useCallback(async () => {
    const { jobId } = reverseConfirmDialog;
    if (!jobId) return;
    try {
      const res = await fetch(`${API_BASE}/invoicing-reverse-complimentary/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(false), 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reverse complimentary.');
      toast.success('Complimentary status reversed.');
      fetchRows();
    } catch (e) {
      toast.error(e.message || 'Failed to reverse complimentary.');
    } finally {
      closeReverseComplimentaryConfirm();
    }
  }, [reverseConfirmDialog, fetchRows]);

  const handlePreview = (row) => {
    if (activeTab !== 'pending' && row.pdf_url) {
      handleFilePreview(row.pdf_url, `Invoice - ${row.invoice_no || row.idf_no || row.id}`);
    } else if (activeTab === 'pending' || !row.is_additional) {
      setPreviewJobId(row.id);
      setPreviewOpen(true);
    }
  };

  const [pendingZFlash, setPendingZFlash] = useState(false);
  useEffect(() => {
    if (!pendingZRows.length) return;
    const id = setInterval(() => setPendingZFlash(f => !f), 700);
    return () => clearInterval(id);
  }, [pendingZRows.length]);

  const previewRow = displayedRows.find(r => r.id === previewJobId);

  const isAllSelected = displayedRows.length > 0 && displayedRows.every(r => selected.includes(r.id));
  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelected(prev => prev.filter(id => !displayedRows.some(r => r.id === id)));
    } else {
      setSelected(prev => [...new Set([...prev, ...displayedRows.map(r => r.id)])]);
    }
  };

  const handleViewJob = async (row) => {
    const id = row.job_id || row.id;
    try {
      const res = await fetch(`${API_BASE}/${id}/`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load job.');
      const data = await res.json();
      setJobView({ job: data, open: true });
    } catch (e) {
      toast.error(e.message || 'Failed to load job.');
    }
  };

  const handleFilePreview = (url, name) => {
    if (!url) return;
    openFilePreview(url, name || 'Document');
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || (!pendingUploadJobId && !pendingUploadAdditionalInvoiceId)) return;
    const uploadedId = pendingUploadAdditionalInvoiceId ? `add-${pendingUploadAdditionalInvoiceId}` : pendingUploadJobId;
    const idx = displayedRows.findIndex(r => r.id === uploadedId);
    const nextId = displayedRows[idx + 1]?.id || null;
    setUploading(true);
    try {
      const formData = new FormData();
      if (pendingUploadAdditionalInvoiceId) {
        formData.append('additional_invoice_id', pendingUploadAdditionalInvoiceId);
      } else {
        formData.append('job_id', pendingUploadJobId);
      }
      formData.append('document_file', file);
      const res = await fetch(`${API_BASE}/invoicing-upload-tax-invoice/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to upload tax invoice.');
      toast.success('KK EFD INVOICE uploaded successfully.');
      await refreshGeneratedRows();
      if (nextId) setHighlightedId(nextId);
    } catch (err) {
      toast.error(err.message || 'Failed to upload tax invoice.');
    } finally {
      setUploading(false);
      setPendingUploadJobId(null);
      setPendingUploadAdditionalInvoiceId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openUpload = (row) => {
    setPendingUploadJobId(row.job_id || row.id);
    setPendingUploadAdditionalInvoiceId(row.additional_invoice_id || null);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditZNumber(row.z_number || '');
    setEditZDate(row.z_date ? String(row.z_date).split('T')[0].split('/').reverse().join('-') : '');
    setEditFile(null);
  };

  const handleEditSave = async () => {
    if (!editRow) return;
    if (!editZNumber.trim()) { toast.error('Z Number is required.'); return; }
    if (!editZDate) { toast.error('Z Date is required.'); return; }
    try {
      const formData = new FormData();
      if (editRow.additional_invoice_id) {
        formData.append('additional_invoice_id', editRow.additional_invoice_id);
      } else {
        formData.append('job_id', editRow.id);
      }
      formData.append('z_number', editZNumber.trim());
      formData.append('z_date', editZDate);
      if (editFile) formData.append('document_file', editFile);
      const res = await fetch(`${API_BASE}/invoicing-edit-z/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save changes.');
      toast.success('Invoice Z details updated.');
      setEditRow(null);
      setEditZNumber('');
      setEditZDate('');
      setEditFile(null);
      fetchRows();
    } catch (err) {
      toast.error(err.message || 'Failed to save changes.');
    }
  };

  const handleUpdateZ = async () => {
    if (!zNumber.trim()) { toast.error('Z Number is required.'); return; }
    if (!zDate) { toast.error('Z Date is required.'); return; }
    if (!selected.length) { toast.error('Select at least one invoice.'); return; }
    try {
      const res = await fetch(`${API_BASE}/invoicing-update-z/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ job_ids: selected, z_number: zNumber.trim(), z_date: zDate }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update Z details.');
      toast.success(`Z Number updated for ${data.updated || selected.length} invoice(s).`);
      setSelected([]);
      setZNumber('');
      setZDate('');
      setZModalOpen(false);
      fetchRows();
    } catch (e) {
      toast.error(e.message || 'Failed to update Z details.');
    }
  };

  const handleOpenZModal = () => {
    const selectedRows = displayedRows.filter(r => selected.includes(r.id));
    const missing = selectedRows.some(r => !r.tax_invoice_url);
    if (missing) {
      toast.error('Please upload KK EFD INVOICE for all selected records before adding Z Number.');
      return;
    }
    setZModalOpen(true);
  };

  const renderInvoiceRow = (row, idx) => (
    <tr
      key={row.id}
      onMouseEnter={() => setHoveredRow(row.id)}
      onMouseLeave={() => setHoveredRow(null)}
      style={{ backgroundColor: row.id === highlightedId ? '#fff3cd' : (row.id === hoveredRow ? '#bbdefb' : (idx % 2 === 0 ? '#e3f2fd' : '#fff')) }}
    >
      {activeTab === 'pendingZ' && (
        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
          <input type="checkbox" checked={selected.includes(row.id)} onChange={() => toggleSelect(row.id)} />
        </td>
      )}
      <td style={cellStyle(row.idf_no)}>
        {activeTab === 'generated' || activeTab === 'pendingZ' ? (
          <a
            href="#"
            style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => { e.preventDefault(); handleViewJob(row); }}
          >
            {toDisplay(row.idf_no)}
          </a>
        ) : toDisplay(row.idf_no)}
      </td>
      <td style={cellStyle(row.bl)}>
        {(() => {
          const blDoc = getBlDocument(row);
          if (blDoc) {
            return (
              <a
                href="#"
                style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={(e) => { e.preventDefault(); handleFilePreview(blDoc.url, blDoc.name); }}
              >
                {toDisplay(row.bl)}
              </a>
            );
          }
          return toDisplay(row.bl);
        })()}
      </td>
      <td style={cellStyle(row.customer_name)}><strong>{toDisplay(row.customer_name)}</strong></td>
      <td style={cellStyle(row.container_size)}>{row.container_size || '-'}</td>
      <td style={cellStyle(row.shipment_type)}>{toDisplay(row.shipment_type)}</td>
      <td style={cellStyle(row.icd)}>{toDisplay(row.icd)}</td>
      <td style={cellStyle(row.transporter)}>{toDisplay(row.transporter)}</td>
      {activeTab === 'pending' && <td style={cellStyle(row.staff)}>{toDisplay(row.staff)}</td>}
      {activeTab !== 'pending' && showViewExpenses && (
        <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
          <button
            className="add-btn"
            title="View Expenses"
            style={{ background: '#6f42c1', color: '#fff', padding: '3px 10px', fontSize: 11, border: 'none', borderRadius: 4, cursor: 'pointer' }}
            onClick={() => setExpensesModal({ jobId: row.id, idfNo: row.idf_no })}
          >
            View
          </button>
        </td>
      )}
      {activeTab !== 'pending' && <td style={cellStyle(row.invoice_no)}>{toDisplay(row.invoice_no)}</td>}
      {activeTab !== 'pending' && <td style={{ ...cellStyle(row.subtotal), textAlign: 'right' }}>{row.subtotal || '-'}</td>}
      {activeTab !== 'pending' && <td style={{ ...cellStyle(row.vat), textAlign: 'right' }}>{row.vat || '-'}</td>}
      {activeTab !== 'pending' && <td style={{ ...cellStyle(row.total_amount), textAlign: 'right' }}>{row.total_amount || '-'}</td>}
      {activeTab !== 'pending' && <td style={cellStyle(row.generated_by)}>{toDisplay(row.generated_by)}</td>}
      {activeTab !== 'pending' && <td style={cellStyle(row.generated_at)}>{formatDateDDMMYYYY(row.generated_at)}</td>}
      {activeTab !== 'pending' && <td style={cellStyle(row.z_number)}>{toDisplay(row.z_number)}</td>}
      {activeTab !== 'pending' && <td style={cellStyle(row.z_date)}>{formatDateDDMMYYYY(row.z_date)}</td>}
      {activeTab === 'pending' && <td style={cellStyle(row.release_date)}>{toDisplay(row.release_date)}</td>}
      <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
        <button
          className="add-btn"
          title="View Invoice"
          style={{ background: 'transparent', color: '#007bff', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer', marginRight: 4 }}
          onClick={() => handlePreview(row)}
        >
          {activeTab === 'pending' ? '+' : '👁'}
        </button>

        {activeTab === 'pending' && canDelete && (
          <button
            className="add-btn"
            title="Complimentary"
            style={{ background: 'transparent', color: '#17a2b8', width: 'auto', height: 28, padding: '2px 6px', fontSize: 11, border: 'none', cursor: 'pointer', marginRight: 4, whiteSpace: 'nowrap' }}
            onClick={() => openComplimentaryConfirm(row.id)}
          >
            Complimentary
          </button>
        )}

        {(activeTab === 'generated' || activeTab === 'pendingZ') && row.tax_invoice_url && (
          <button
            className="add-btn"
            title="View KK EFD INVOICE"
            style={{ background: 'transparent', color: '#28a745', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer', marginRight: 4 }}
            onClick={() => handleFilePreview(row.tax_invoice_url, `KK EFD INVOICE - ${row.invoice_no || row.id}`)}
          >
            👁
          </button>
        )}
        {activeTab === 'pendingZ' && canCreate && (
          <button
            className="add-btn"
            title="Upload KK EFD INVOICE"
            style={{ background: 'transparent', color: '#6f42c1', width: 28, height: 28, padding: 0, border: 'none', cursor: 'pointer', marginRight: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => openUpload(row)}
            disabled={uploading}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" style={{ pointerEvents: 'none' }}>
              <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" />
            </svg>
          </button>
        )}
        {activeTab === 'generated' && canDelete && !row.complimentary && (
          <button
            className="add-btn"
            title="Edit Z Details"
            style={{ background: 'transparent', color: '#fd7e14', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer' }}
            onClick={() => openEdit(row)}
          >
            ✎
          </button>
        )}
        {activeTab === 'generated' && canDelete && !row.complimentary && (
          <button
            className="add-btn"
            title="Create Additional Invoice"
            style={{ background: 'transparent', color: '#007bff', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer' }}
            onClick={() => openAdditionalInvoice(row)}
          >
            +
          </button>
        )}
        {activeTab === 'complimentary' && canDelete && (
          <button
            className="add-btn"
            title="Reverse Complimentary"
            style={{ background: 'transparent', color: '#dc3545', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer' }}
            onClick={() => openReverseComplimentaryConfirm(row.id)}
          >
            <i className="fa fa-undo" />
          </button>
        )}
      </td>
    </tr>
  );

  const exportGeneratedToExcel = useCallback(() => {
    const isPrevious = activeTab === 'generated' && generatedPage === 'previous';
    const rowsToExport = isPrevious
      ? previousMonthGroups.flatMap(g => g.rows)
      : (activeTab === 'generated' ? currentMonthRows : []);

    if (!rowsToExport.length) {
      toast.info('No invoices to export.');
      return;
    }

    const headers = ['IDF NO', 'CUSTOMER', 'INVOICE NO', 'SUBTOTAL', 'VAT', 'GROSS AMT', 'Z NUMBER', 'Z DATE'];

    const border = {
      top: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
    };

    const headerCell = (value, right = false) => {
      const style = {
        border,
        font: { bold: true },
        fill: { patternType: 'solid', fgColor: { rgb: 'ADD8E6' } },
      };
      if (right) style.alignment = { horizontal: 'right' };
      return { v: value, s: style };
    };

    const dataCell = (value, bold = false, right = false) => {
      const style = { border, font: { bold } };
      if (right) style.alignment = { horizontal: 'right' };
      return { v: value, s: style };
    };

    const totalAmountCell = (value) => {
      const isNegative = value < 0;
      const formatted = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const display = isNegative ? `(${formatted})` : formatted;
      return { v: display, s: { border, font: { bold: true }, alignment: { horizontal: 'right' } } };
    };

    const sortedRows = [...rowsToExport].sort((a, b) => {
      const dateA = parseGeneratedDate(a.z_date);
      const dateB = parseGeneratedDate(b.z_date);
      const timeA = dateA ? dateA.getTime() : Infinity;
      const timeB = dateB ? dateB.getTime() : Infinity;
      if (timeA !== timeB) return timeA - timeB;
      const zCmp = String(a.z_number || '').localeCompare(String(b.z_number || ''), undefined, { numeric: true });
      if (zCmp !== 0) return zCmp;
      return String(a.invoice_no || '').localeCompare(String(b.invoice_no || ''), undefined, { numeric: true });
    });

    const data = [headers.map((h, i) => headerCell(h, [3, 4, 5].includes(i)))];
    let totalSubtotal = 0;
    let totalVat = 0;
    let totalNet = 0;

    sortedRows.forEach(row => {
      totalSubtotal += parseAmount(row.subtotal);
      totalVat += parseAmount(row.vat);
      totalNet += parseAmount(row.total_amount);
      data.push([
        dataCell(toDisplay(row.idf_no)),
        dataCell(toDisplay(row.customer_name)),
        dataCell(toDisplay(row.invoice_no)),
        dataCell(row.subtotal || '-', false, true),
        dataCell(row.vat || '-', false, true),
        dataCell(row.total_amount || '-', false, true),
        dataCell(toDisplay(row.z_number)),
        dataCell(formatDateDDMMYYYY(row.z_date)),
      ]);
    });

    data.push([
      { v: 'TOTAL', s: { border, font: { bold: true }, alignment: { horizontal: 'center', vertical: 'center' } } },
      dataCell(''),
      dataCell(''),
      totalAmountCell(totalSubtotal),
      totalAmountCell(totalVat),
      totalAmountCell(totalNet),
      dataCell(''),
      dataCell(''),
    ]);

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Generated Invoices');

    const lastRow = data.length - 1;
    ws['!merges'] = [{ s: { r: lastRow, c: 0 }, e: { r: lastRow, c: 2 } }];

    const colCount = data[0].length;
    ws['!cols'] = Array.from({ length: colCount }, (_, colIdx) => {
      const maxLen = data.reduce((max, row) => Math.max(max, String(row[colIdx]?.v ?? '').length), 0);
      return { wch: maxLen + 2 };
    });

    const filename = isPrevious ? 'previous_generated_invoices.xlsx' : 'current_generated_invoices.xlsx';
    XLSX.writeFile(wb, filename);
  }, [activeTab, generatedPage, currentMonthRows, previousMonthGroups]);

  if (!canView) {
    return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Invoicing.</p></div>;
  }

  return (
    <div className="client-reports-page" style={{ padding: 16 }}>
      <div className="list-header">
        <h2 style={{ margin: 0, color: '#333' }}>Invoicing</h2>
      </div>

      <div className="toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 260 }}>
            <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>CUSTOMER</label>
            <Select2Dropdown
              options={customers}
              value={customerId}
              onChange={handleCustomerChange}
              placeholder="All Customers"
              displayKey="text"
              valueKey="id"
              clearable
              menuPlacement="auto"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>SEARCH (IDF / BL)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                className="search-input"
                placeholder="Search IDF or BL..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleApplySearch(); }}
                style={{ width: 220 }}
              />
              {canSearchNonReleased && (
                <div style={{ alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}>
                  <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>SEARCH NON-RELEASED</label>
                  <input
                    type="checkbox"
                    id="search-non-released"
                    checked={searchNonReleased}
                    onChange={e => setSearchNonReleased(e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  
                </div>
              )}
            </div>
          </div>
          <button className="add-btn" style={{ background: '#007bff' }} onClick={handleApplySearch}>Search</button>
          <button className="add-btn" style={{ background: '#6c757d' }} onClick={handleClear}>Clear</button>
          <button className="add-btn" style={{ background: '#3f6b32' }} onClick={fetchRows}>Refresh</button>
        </div>
        {activeTab === 'generated' && !activeTabLoading && (
          <div style={{ display: 'flex', gap: 12, marginLeft: 'auto' }}>
            <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '12px 18px', minWidth: 320 }}>
              <div style={{ fontSize: 14, color: '#1565c0', fontWeight: 700, marginBottom: 4 }}>Selected Date Range Summary</div>
              <div style={{ fontSize: 12, color: '#0d47a1', fontWeight: 600, marginBottom: 10 }}>
                {formatISODate(generatedStartDate)}{generatedStartDate !== generatedEndDate && ` - ${formatISODate(generatedEndDate)}`}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #bbdefb', padding: '5px 0' }}>
                <span style={{ fontSize: 14, color: '#0d47a1' }}>Generated Invoices</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#0d47a1' }}>{generatedDateRangeSummary.count}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #bbdefb', padding: '5px 0' }}>
                <span style={{ fontSize: 14, color: '#0d47a1' }}>Net Amount</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#0d47a1' }}>{formatSummaryAmount(generatedDateRangeSummary.net)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #bbdefb', padding: '5px 0' }}>
                <span style={{ fontSize: 14, color: '#0d47a1' }}>VAT</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#0d47a1' }}>{formatSummaryAmount(generatedDateRangeSummary.vat)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
                <span style={{ fontSize: 14, color: '#0d47a1' }}>Gross Amount</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#0d47a1' }}>{formatSummaryAmount(generatedDateRangeSummary.gross)}</span>
              </div>
            </div>
            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '12px 18px', minWidth: 320 }}>
              <div style={{ fontSize: 14, color: '#388e3c', fontWeight: 700, marginBottom: 4 }}>Generated Invoices Summary</div>
              <div style={{ fontSize: 12, color: '#1b5e20', fontWeight: 600, marginBottom: 10 }}>
                {firstGeneratedDate ? `${formatISODate(firstGeneratedDate)} - ${formatISODate(todayISO())}` : 'No generated invoices'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #c8e6c9', padding: '5px 0' }}>
                <span style={{ fontSize: 14, color: '#1b5e20' }}>Generated Invoices</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1b5e20' }}>{generatedSummary.count}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #c8e6c9', padding: '5px 0' }}>
                <span style={{ fontSize: 14, color: '#1b5e20' }}>Net Amount</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1b5e20' }}>{formatSummaryAmount(generatedSummary.net)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #c8e6c9', padding: '5px 0' }}>
                <span style={{ fontSize: 14, color: '#1b5e20' }}>VAT</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1b5e20' }}>{formatSummaryAmount(generatedSummary.vat)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
                <span style={{ fontSize: 14, color: '#1b5e20' }}>Gross Amount</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#1b5e20' }}>{formatSummaryAmount(generatedSummary.gross)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="custom-tabs-container" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex' }}>
          <button
            className={`custom-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => { setActiveTab('pending'); setSelected([]); }}
          >
            Pending ({pendingRows.length})
          </button>
          <button
            className={`custom-tab ${activeTab === 'pendingZ' ? 'active' : ''}`}
            onClick={() => { setActiveTab('pendingZ'); setSelected([]); }}
            style={activeTab !== 'pendingZ' && pendingZRows.length > 0 ? { backgroundColor: pendingZFlash ? '#ffeeba' : '#fff3cd', color: '#856404', fontWeight: 'bold', transition: 'background-color 0.3s' } : undefined}
          >
            Z Report ({pendingZRows.length})
          </button>
          <button
            className={`custom-tab ${activeTab === 'generated' ? 'active' : ''}`}
            onClick={() => { setActiveTab('generated'); setSelected([]); }}
          >
            Generated ({completedGeneratedRows.length})
          </button>
          <button
            className={`custom-tab ${activeTab === 'complimentary' ? 'active' : ''}`}
            onClick={() => { setActiveTab('complimentary'); setSelected([]); }}
          >
            Complimentary ({complimentaryRows.length})
          </button>
        </div>
        {canEdit && (
          <button
            type="button"
            className="btn-icon"
            title="Accounts Settings"
            onClick={() => setSettingsOpen(true)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: '#6b7280', padding: '4px 8px' }}
          >
            <i className="fa fa-cog"></i>
          </button>
        )}
      </div>

      {activeTab === 'generated' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 12, padding: '8px 12px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>Generated Invoices:</span>
          <button
            className="add-btn"
            style={{ background: generatedPage === 'current' ? '#007bff' : '#6c757d', fontSize: 12 }}
            onClick={() => setGeneratedPage('current')}
          >
            Page 1 — Current Month
          </button>
          <button
            className="add-btn"
            style={{ background: generatedPage === 'previous' ? '#007bff' : '#6c757d', fontSize: 12 }}
            onClick={() => setGeneratedPage('previous')}
          >
            Page 2 — Previous Months
          </button>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>VIEW MODE</label>
              <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #ced4da' }}>
                {GENERATED_VIEW_MODES.map(mode => (
                  <button
                    key={mode.id}
                    onClick={() => setGeneratedViewMode(mode.id)}
                    style={{
                      padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '0.85rem',
                      background: generatedViewMode === mode.id ? '#1a73e8' : '#fff',
                      color: generatedViewMode === mode.id ? '#fff' : '#333',
                      fontWeight: generatedViewMode === mode.id ? 600 : 400,
                    }}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>

            {generatedViewMode === 'range' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ marginBottom: 0, fontSize: '0.9em' }}>From:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={generatedStartDate}
                    onChange={e => setGeneratedStartDate(e.target.value)}
                    style={{ width: 120 }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <label style={{ marginBottom: 0, fontSize: '0.9em' }}>To:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={generatedEndDate}
                    onChange={e => setGeneratedEndDate(e.target.value)}
                    style={{ width: 120 }}
                  />
                </div>
              </>
            )}
          </div>
          <button
            className="add-btn"
            title="Export to Excel"
            style={{ marginLeft: 'auto', background: '#28a745', color: '#fff', fontSize: 12, display: 'flex', alignItems: 'center' }}
            onClick={exportGeneratedToExcel}
          >
            <i className="fa fa-file-excel" style={{ marginRight: 6 }} /> Excel
          </button>
        </div>
      )}

      {activeTabLoading && <div className="loading-indicator">Loading...</div>}

      {activeTab === 'pendingZ' && !activeTabLoading && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '12px 18px', marginBottom: 12, display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ color: '#856404' }}>Selected Invoices: {selectedSummary.count}</strong>
          <span style={{ color: '#856404' }}>Net: {formatSummaryAmount(selectedSummary.net)}</span>
          <span style={{ color: '#856404' }}>VAT: {formatSummaryAmount(selectedSummary.vat)}</span>
          <span style={{ color: '#856404' }}>Gross: {formatSummaryAmount(selectedSummary.gross)}</span>
        </div>
      )}
      {!activeTabLoading && (
        <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
            <thead>
              <tr>
                {activeTab === 'pendingZ' && (
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', width: 40 }}>
                    <input type="checkbox" checked={isAllSelected} onChange={toggleSelectAll} />
                  </th>
                )}
                <th onClick={() => handleSort('idf_no')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>IDF{sortColumn === 'idf_no' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>
                <th onClick={() => handleSort('bl')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>BL{sortColumn === 'bl' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>
                <th onClick={() => handleSort('customer_name')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>CUSTOMER{sortColumn === 'customer_name' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>
                <th onClick={() => handleSort('container_size')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>CONT. SIZE{sortColumn === 'container_size' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>
                <th onClick={() => handleSort('shipment_type')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>SHIPMENT TYPE{sortColumn === 'shipment_type' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>
                <th onClick={() => handleSort('icd')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>ICD{sortColumn === 'icd' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>
                <th onClick={() => handleSort('transporter')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>TRANSPORTER{sortColumn === 'transporter' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>
                {activeTab === 'pending' && <th onClick={() => handleSort('staff')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>STAFF{sortColumn === 'staff' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab !== 'pending' && showViewExpenses && <th style={{ border: '1px solid #ddd', padding: '6px', whiteSpace: 'nowrap' }}>EXPENSES</th>}
                {activeTab !== 'pending' && <th onClick={() => handleSort('invoice_no')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>INVOICE NO{sortColumn === 'invoice_no' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab !== 'pending' && <th onClick={() => handleSort('subtotal')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}>NET AMT.{sortColumn === 'subtotal' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab !== 'pending' && <th onClick={() => handleSort('vat')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}>VAT{sortColumn === 'vat' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab !== 'pending' && <th onClick={() => handleSort('total_amount')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}>GROSS AMT{sortColumn === 'total_amount' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab !== 'pending' && <th onClick={() => handleSort('generated_by')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>GENERATED BY{sortColumn === 'generated_by' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab !== 'pending' && <th onClick={() => handleSort('generated_at')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>GENERATED DATE{sortColumn === 'generated_at' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab !== 'pending' && <th onClick={() => handleSort('z_number')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Z NUMBER{sortColumn === 'z_number' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab !== 'pending' && <th onClick={() => handleSort('z_date')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Z DATE{sortColumn === 'z_date' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                {activeTab === 'pending' && <th onClick={() => handleSort('release_date')} style={{ border: '1px solid #ddd', padding: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>RELEASED DATE{sortColumn === 'release_date' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}</th>}
                <th style={{ border: '1px solid #ddd', padding: '6px', width: 120, whiteSpace: 'nowrap' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {activeTab === 'generated' && generatedPage === 'previous' ? (
                previousMonthGroups.map(group => (
                  <React.Fragment key={group.key}>
                    <tr
                      onClick={() => toggleMonth(group.key)}
                      style={{ cursor: 'pointer', backgroundColor: '#e8f5e9' }}
                    >
                      <td colSpan={20} style={{ border: '1px solid #ddd', padding: '10px 6px', fontSize: 14, fontWeight: 'bold' }}>
                        <span style={{ display: 'inline-block', width: 20 }}>
                          {expandedMonths.has(group.key) ? '−' : '+'}
                        </span>
                        {group.label} ({group.rows.length} invoices)
                      </td>
                    </tr>
                    {expandedMonths.has(group.key) && group.rows.map((row, rIdx) => renderInvoiceRow(row, rIdx))}
                  </React.Fragment>
                ))
              ) : (activeTab === 'generated' ? currentMonthRows : displayedRows).map((row, idx) => (
                <tr
                  key={row.id}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{ backgroundColor: row.id === highlightedId ? '#fff3cd' : (row.id === hoveredRow ? '#bbdefb' : (idx % 2 === 0 ? '#e3f2fd' : '#fff')) }}
                >
                  {activeTab === 'pendingZ' && (
                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                      <input type="checkbox" checked={selected.includes(row.id)} onChange={() => toggleSelect(row.id)} />
                    </td>
                  )}
                  <td style={cellStyle(row.idf_no)}>
                    {activeTab === 'generated' || activeTab === 'pendingZ' ? (
                      <a
                        href="#"
                        style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={(e) => { e.preventDefault(); handleViewJob(row); }}
                      >
                        {toDisplay(row.idf_no)}
                      </a>
                    ) : toDisplay(row.idf_no)}
                  </td>
                  <td style={cellStyle(row.bl)}>
                    {(() => {
                      const blDoc = getBlDocument(row);
                      if (blDoc) {
                        return (
                          <a
                            href="#"
                            style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={(e) => { e.preventDefault(); handleFilePreview(blDoc.url, blDoc.name); }}
                          >
                            {toDisplay(row.bl)}
                          </a>
                        );
                      }
                      return toDisplay(row.bl);
                    })()}
                  </td>
                  <td style={cellStyle(row.customer_name)}><strong>{toDisplay(row.customer_name)}</strong></td>
                  <td style={cellStyle(row.container_size)}>{row.container_size || '-'}</td>
                  <td style={cellStyle(row.shipment_type)}>{toDisplay(row.shipment_type)}</td>
                  <td style={cellStyle(row.icd)}>{toDisplay(row.icd)}</td>
                  <td style={cellStyle(row.transporter)}>{toDisplay(row.transporter)}</td>
                  {activeTab === 'pending' && <td style={cellStyle(row.staff)}>{toDisplay(row.staff)}</td>}
                  {activeTab !== 'pending' && showViewExpenses && (
                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                      <button
                        className="add-btn"
                        title="View Expenses"
                        style={{ background: '#6f42c1', color: '#fff', padding: '3px 10px', fontSize: 11, border: 'none', borderRadius: 4, cursor: 'pointer' }}
                        onClick={() => setExpensesModal({ jobId: row.id, idfNo: row.idf_no })}
                      >
                        View
                      </button>
                    </td>
                  )}
                  {activeTab !== 'pending' && <td style={cellStyle(row.invoice_no)}>{toDisplay(row.invoice_no)}</td>}
                  {activeTab !== 'pending' && <td style={{ ...cellStyle(row.subtotal), textAlign: 'right' }}>{row.subtotal || '-'}</td>}
                  {activeTab !== 'pending' && <td style={{ ...cellStyle(row.vat), textAlign: 'right' }}>{row.vat || '-'}</td>}
                  {activeTab !== 'pending' && <td style={{ ...cellStyle(row.total_amount), textAlign: 'right' }}>{row.total_amount || '-'}</td>}
                  {activeTab !== 'pending' && <td style={cellStyle(row.generated_by)}>{toDisplay(row.generated_by)}</td>}
                  {activeTab !== 'pending' && <td style={cellStyle(row.generated_at)}>{formatDateDDMMYYYY(row.generated_at)}</td>}
                  {activeTab !== 'pending' && <td style={cellStyle(row.z_number)}>{toDisplay(row.z_number)}</td>}
                  {activeTab !== 'pending' && <td style={cellStyle(row.z_date)}>{formatDateDDMMYYYY(row.z_date)}</td>}
                  {activeTab === 'pending' && <td style={cellStyle(row.release_date)}>{toDisplay(row.release_date)}</td>}
                  <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button
                      className="add-btn"
                      title="View Invoice"
                      style={{ background: 'transparent', color: '#007bff', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer', marginRight: 4 }}
                      onClick={() => handlePreview(row)}
                    >
                      {activeTab === 'pending' ? '+' : '👁'}
                    </button>
                    
                    {activeTab === 'pending' && canDelete && (
                      <button
                        className="add-btn"
                        title="Complimentary"
                        style={{ background: 'transparent', color: '#17a2b8', width: 'auto', height: 28, padding: '2px 6px', fontSize: 11, border: 'none', cursor: 'pointer', marginRight: 4, whiteSpace: 'nowrap' }}
                        onClick={() => openComplimentaryConfirm(row.id)}
                      >
                        Complimentary
                      </button>
                    )}
                    
                    {(activeTab === 'generated' || activeTab === 'pendingZ') && row.tax_invoice_url && (
                      <button
                        className="add-btn"
                        title="View KK EFD INVOICE"
                        style={{ background: 'transparent', color: '#28a745', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer', marginRight: 4 }}
                        onClick={() => handleFilePreview(row.tax_invoice_url, `KK EFD INVOICE - ${row.invoice_no || row.id}`)}
                      >
                        👁
                      </button>
                    )}
                    {activeTab === 'pendingZ' && canCreate && (
                      <button
                        className="add-btn"
                        title="Upload KK EFD INVOICE"
                        style={{ background: 'transparent', color: '#6f42c1', width: 28, height: 28, padding: 0, border: 'none', cursor: 'pointer', marginRight: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={() => openUpload(row)}
                        disabled={uploading}
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" style={{ pointerEvents: 'none' }}>
                          <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" />
                        </svg>
                      </button>
                    )}
                    {activeTab === 'generated' && canDelete && !row.complimentary && (
                      <button
                        className="add-btn"
                        title="Edit Z Details"
                        style={{ background: 'transparent', color: '#fd7e14', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer' }}
                        onClick={() => openEdit(row)}
                      >
                        ✎
                      </button>
                    )}
                    {activeTab === 'generated' && canDelete && !row.complimentary && (
                      <button
                        className="add-btn"
                        title="Create Additional Invoice"
                        style={{ background: 'transparent', color: '#007bff', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer' }}
                        onClick={() => openAdditionalInvoice(row)}
                      >
                        +
                      </button>
                    )}
                    {activeTab === 'complimentary' && canDelete && (
                      <button
                        className="add-btn"
                        title="Reverse Complimentary"
                        style={{ background: 'transparent', color: '#dc3545', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer' }}
                        onClick={() => openReverseComplimentaryConfirm(row.id)}
                      >
                        <i className="fa fa-undo" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(
                (activeTab === 'generated' && generatedPage === 'previous' && previousMonthGroups.length === 0) ||
                (activeTab === 'generated' && generatedPage === 'current' && currentMonthRows.length === 0) ||
                (activeTab !== 'generated' && displayedRows.length === 0)
              ) && (
                <tr><td colSpan={20} style={{ border: '1px solid #ddd', padding: '6px' }} className="no-data">No records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'pendingZ' && canCreate && (
        <div style={{ marginTop: 12 }}>
          <button
            className="add-btn"
            style={{ background: '#28a745' }}
            onClick={handleOpenZModal}
            disabled={!selected.length}
          >
            Add Z Number {selected.length > 0 && `(${selected.length} selected)`}
          </button>
        </div>
      )}

      {previewOpen && previewJobId && (
        <PopoutWindow
          name="InvoicePreview"
          title="Invoice Preview"
          width={1100}
          height={800}
          onClose={closePreview}
          toastContainerId="InvoicePreviewToasts"
        >
          <InvoicePreviewDialog
            key={previewJobId}
            jobId={previewJobId}
            isPending={activeTab === 'pending'}
            canCreate={canCreate}
            canDelete={canDelete}
            shipmentType={previewRow?.shipment_type}
            onClose={closePreview}
            onGenerated={handleGenerated}
          />
        </PopoutWindow>
      )}

      {additionalOpen && additionalRow && (
        <PopoutWindow
          name={`AdditionalInvoice_${additionalRow.id}`}
          title={`Additional Invoice - ${additionalRow.idf_no || additionalRow.id}`}
          width={1100}
          height={800}
          onClose={closeAdditional}
          toastContainerId="InvoicePreviewToasts"
        >
          <InvoicePreviewDialog
            key={`additional-${additionalRow.id}`}
            additionalInvoice
            preSelectedJob={additionalRow}
            canCreate={canCreate}
            canDelete={canDelete}
            shipmentType={additionalRow?.shipment_type}
            onClose={closeAdditional}
            onGenerated={handleAdditionalGenerated}
          />
        </PopoutWindow>
      )}

      {expensesModal && (
        <PopoutWindow
          title={`Expenses - IDF #${expensesModal.idfNo || ''}`}
          name={`job_expenses_${(expensesModal.idfNo || expensesModal.jobId || '').toString().replace(/\s+/g, '_')}`}
          onClose={() => setExpensesModal(null)}
        >
          <PayableExpensesView
            idfNo={expensesModal.idfNo}
            jobId={expensesModal.jobId}
            fromInvoicing={true}
            onClose={() => setExpensesModal(null)}
          />
        </PopoutWindow>
      )}

      {zModalOpen && (
        <PopoutWindow
          name="AddZNumber"
          title="Add Z Number"
          width={400}
          height={300}
          onClose={() => setZModalOpen(false)}
          toastContainerId="AddZNumberToasts"
        >
          <div style={{ display: 'flex', flexDirection: 'column', padding: 16, gap: 12 }}>
            <h4 style={{ margin: 0 }}>Z Number</h4>
            <input
              type="text"
              value={zNumber}
              onChange={e => setZNumber(e.target.value)}
              placeholder="Enter Z Number"
              style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4 }}
            />
            <h4 style={{ margin: 0 }}>Z Date</h4>
            <input
              type="date"
              value={zDate}
              onChange={e => setZDate(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button className="add-btn" style={{ background: '#6c757d' }} onClick={() => setZModalOpen(false)}>Cancel</button>
              <button className="add-btn" style={{ background: '#28a745' }} onClick={handleUpdateZ}>Save</button>
            </div>
          </div>
        </PopoutWindow>
      )}

      <input type="file" ref={fileInputRef} onChange={handleFileSelected} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />

      {editRow && (
        <PopoutWindow
          name={`EditZ_${editRow.id}`}
          title={`Edit Invoice - ${editRow.invoice_no || editRow.id}`}
          width={450}
          height={420}
          onClose={() => { setEditRow(null); setEditFile(null); }}
          toastContainerId="EditZToasts"
        >
          <div style={{ display: 'flex', flexDirection: 'column', padding: 16, gap: 12 }}>
            <h4 style={{ margin: 0 }}>Z Number</h4>
            <input
              type="text"
              value={editZNumber}
              onChange={e => setEditZNumber(e.target.value)}
              placeholder="Enter Z Number"
              style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4 }}
            />
            <h4 style={{ margin: 0 }}>Z Date</h4>
            <input
              type="date"
              value={editZDate}
              onChange={e => setEditZDate(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4 }}
            />
            <h4 style={{ margin: 0 }}>KK EFD INVOICE (Replace)</h4>
            <input
              type="file"
              onChange={e => setEditFile(e.target.files ? e.target.files[0] : null)}
              style={{ fontSize: 13 }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button className="add-btn" style={{ background: '#6c757d' }} onClick={() => { setEditRow(null); setEditFile(null); }}>Cancel</button>
              <button className="add-btn" style={{ background: '#28a745' }} onClick={handleEditSave}>Save</button>
            </div>
          </div>
        </PopoutWindow>
      )}

      {jobView?.open && (
        <PopoutWindow
          name={`JobView_${jobView.job.id}`}
          title={`Job - ${jobView.job.idf_no || jobView.job.id}`}
          width={1200}
          height={900}
          onClose={() => setJobView(null)}
          toastContainerId="JobViewToasts"
        >
          <JobForm
            mode="view"
            initialData={jobView.job}
            onClose={() => setJobView(null)}
          />
        </PopoutWindow>
      )}

      {settingsOpen && (
        <PopoutWindow
          name="AccountsSettings_Invoicing"
          title="Accounts Settings"
          width={1200}
          height={800}
          zoom={1}
          onClose={() => setSettingsOpen(false)}
        >
          <AccountsSettings />
        </PopoutWindow>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title="Complimentary"
        message="Are you sure you would like to mark this Invoice as Complimentary?"
        onConfirm={handleMarkComplimentary}
        onCancel={closeComplimentaryConfirm}
      />

      <ConfirmDialog
        open={reverseConfirmDialog.open}
        title="Reverse Complimentary"
        message="Are you sure you want to reverse the complimentary status for this invoice?"
        onConfirm={handleReverseComplimentary}
        onCancel={closeReverseComplimentaryConfirm}
      />
    </div>
  );
};

export default Invoicing;
